/*!
 * Copyright (c) 2017-Present, Okta, Inc. and/or its affiliates. All rights reserved.
 * The Okta software accompanied by this notice is provided pursuant to the Apache License, Version 2.0 (the "License.")
 *
 * You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0.
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *
 * See the License for the specific language governing permissions and limitations under the License.
 */
import { InjectionToken, Injectable, Inject, Component, NgModule } from '@angular/core';
import { __awaiter, __generator } from 'tslib';
import { Router } from '@angular/router';
import { buildConfigObject, assertIssuer, assertClientId, assertRedirectUri } from '@okta/configuration-validation';
import OktaAuth from '@okta/okta-auth-js';
import { Observable } from 'rxjs';

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/** @type {?} */
var OKTA_CONFIG = new InjectionToken('okta.config.angular');

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var packageInfo = {
    'name': '@okta/okta-angular',
    'version': '1.4.0'
};

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var OktaAuthService = /** @class */ (function () {
    function OktaAuthService(auth, router) {
        var _this = this;
        this.auth = auth;
        this.router = router;
        this.observers = [];
        /**
         * Cache the auth config.
         */
        this.config = buildConfigObject(auth); // use normalized config object
        this.config.scopes = this.config.scopes || [];
        // Automatically enter login flow if session has expired or was ended outside the application
        // The default behavior can be overriden by setting your own `onSessionExpired` function on the OktaConfig
        if (!this.config.onSessionExpired) {
            this.config.onSessionExpired = this.login.bind(this);
        }
        /**
         * Scrub scopes to ensure 'openid' is included
         */
        this.scrubScopes(this.config.scopes);
        // Assert Configuration
        assertIssuer(this.config.issuer, this.config.testing);
        assertClientId(this.config.clientId);
        assertRedirectUri(this.config.redirectUri);
        this.oktaAuth = new OktaAuth(this.config);
        this.oktaAuth.userAgent = packageInfo.name + "/" + packageInfo.version + " " + this.oktaAuth.userAgent;
        this.$authenticationState = new Observable(function (observer) { _this.observers.push(observer); });
    }
    /**
     * @param {?=} fromUri
     * @param {?=} additionalParams
     * @return {?}
     */
    OktaAuthService.prototype.login = /**
     * @param {?=} fromUri
     * @param {?=} additionalParams
     * @return {?}
     */
    function (fromUri, additionalParams) {
        this.setFromUri(fromUri || this.router.url);
        /** @type {?} */
        var onAuthRequired = this.getOktaConfig().onAuthRequired;
        if (onAuthRequired) {
            return onAuthRequired(this, this.router);
        }
        return this.loginRedirect(undefined, additionalParams);
    };
    /**
     * @return {?}
     */
    OktaAuthService.prototype.getTokenManager = /**
     * @return {?}
     */
    function () {
        return this.oktaAuth.tokenManager;
    };
    /**
     * Checks if there is an access token and id token
     */
    /**
     * Checks if there is an access token and id token
     * @return {?}
     */
    OktaAuthService.prototype.isAuthenticated = /**
     * Checks if there is an access token and id token
     * @return {?}
     */
    function () {
        return __awaiter(this, void 0, void 0, function () {
            var accessToken, idToken;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        // Support a user-provided method to check authentication
                        if (this.config.isAuthenticated) {
                            return [2 /*return*/, (this.config.isAuthenticated)()];
                        }
                        return [4 /*yield*/, this.getAccessToken()];
                    case 1:
                        accessToken = _a.sent();
                        return [4 /*yield*/, this.getIdToken()];
                    case 2:
                        idToken = _a.sent();
                        return [2 /*return*/, !!(accessToken || idToken)];
                }
            });
        });
    };
    /**
     * @param {?} state
     * @return {?}
     */
    OktaAuthService.prototype.emitAuthenticationState = /**
     * @param {?} state
     * @return {?}
     */
    function (state) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                this.observers.forEach(function (observer) { return observer.next(state); });
                return [2 /*return*/];
            });
        });
    };
    /**
     * Returns the current accessToken in the tokenManager.
     */
    /**
     * Returns the current accessToken in the tokenManager.
     * @return {?}
     */
    OktaAuthService.prototype.getAccessToken = /**
     * Returns the current accessToken in the tokenManager.
     * @return {?}
     */
    function () {
        return __awaiter(this, void 0, void 0, function () {
            var accessToken, err_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, this.oktaAuth.tokenManager.get('accessToken')];
                    case 1:
                        accessToken = _a.sent();
                        return [2 /*return*/, accessToken.accessToken];
                    case 2:
                        err_1 = _a.sent();
                        // The user no longer has an existing SSO session in the browser.
                        // (OIDC error `login_required`)
                        // Ask the user to authenticate again.
                        return [2 /*return*/, undefined];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Returns the current idToken in the tokenManager.
     */
    /**
     * Returns the current idToken in the tokenManager.
     * @return {?}
     */
    OktaAuthService.prototype.getIdToken = /**
     * Returns the current idToken in the tokenManager.
     * @return {?}
     */
    function () {
        return __awaiter(this, void 0, void 0, function () {
            var idToken, err_2;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, this.oktaAuth.tokenManager.get('idToken')];
                    case 1:
                        idToken = _a.sent();
                        return [2 /*return*/, idToken.idToken];
                    case 2:
                        err_2 = _a.sent();
                        // The user no longer has an existing SSO session in the browser.
                        // (OIDC error `login_required`)
                        // Ask the user to authenticate again.
                        return [2 /*return*/, undefined];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Returns user claims from the /userinfo endpoint if an
     * accessToken is provided or parses the available idToken.
     */
    /**
     * Returns user claims from the /userinfo endpoint if an
     * accessToken is provided or parses the available idToken.
     * @return {?}
     */
    OktaAuthService.prototype.getUser = /**
     * Returns user claims from the /userinfo endpoint if an
     * accessToken is provided or parses the available idToken.
     * @return {?}
     */
    function () {
        return __awaiter(this, void 0, void 0, function () {
            var accessToken, idToken, userinfo;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.oktaAuth.tokenManager.get('accessToken')];
                    case 1:
                        accessToken = _a.sent();
                        return [4 /*yield*/, this.oktaAuth.tokenManager.get('idToken')];
                    case 2:
                        idToken = _a.sent();
                        if (!(accessToken && idToken)) return [3 /*break*/, 4];
                        return [4 /*yield*/, this.oktaAuth.token.getUserInfo(accessToken)];
                    case 3:
                        userinfo = _a.sent();
                        if (userinfo.sub === idToken.claims.sub) {
                            // Only return the userinfo response if subjects match to
                            // mitigate token substitution attacks
                            return [2 /*return*/, userinfo];
                        }
                        _a.label = 4;
                    case 4: return [2 /*return*/, idToken ? idToken.claims : undefined];
                }
            });
        });
    };
    /**
     * Returns the configuration object used.
     */
    /**
     * Returns the configuration object used.
     * @return {?}
     */
    OktaAuthService.prototype.getOktaConfig = /**
     * Returns the configuration object used.
     * @return {?}
     */
    function () {
        return this.config;
    };
    /**
     * Launches the login redirect.
     * @param fromUri
     * @param additionalParams
     */
    /**
     * Launches the login redirect.
     * @param {?=} fromUri
     * @param {?=} additionalParams
     * @return {?}
     */
    OktaAuthService.prototype.loginRedirect = /**
     * Launches the login redirect.
     * @param {?=} fromUri
     * @param {?=} additionalParams
     * @return {?}
     */
    function (fromUri, additionalParams) {
        if (fromUri) {
            this.setFromUri(fromUri);
        }
        // Normalize params, set defaults
        /** @type {?} */
        var params = buildConfigObject(additionalParams);
        params.scopes = params.scopes || this.config.scopes;
        params.responseType = params.responseType
            || this.config.responseType
            || ['id_token', 'token'];
        return this.oktaAuth.token.getWithRedirect(params); // can throw
    };
    /**
     * Stores the intended path to redirect after successful login.
     * @param uri
     * @param queryParams
     */
    /**
     * Stores the intended path to redirect after successful login.
     * @param {?} uri
     * @param {?=} queryParams
     * @return {?}
     */
    OktaAuthService.prototype.setFromUri = /**
     * Stores the intended path to redirect after successful login.
     * @param {?} uri
     * @param {?=} queryParams
     * @return {?}
     */
    function (uri, queryParams) {
        /** @type {?} */
        var json = JSON.stringify({
            uri: uri,
            params: queryParams
        });
        localStorage.setItem('referrerPath', json);
    };
    /**
     * Returns the referrer path from localStorage or app root.
     */
    /**
     * Returns the referrer path from localStorage or app root.
     * @return {?}
     */
    OktaAuthService.prototype.getFromUri = /**
     * Returns the referrer path from localStorage or app root.
     * @return {?}
     */
    function () {
        /** @type {?} */
        var referrerPath = localStorage.getItem('referrerPath');
        localStorage.removeItem('referrerPath');
        /** @type {?} */
        var path;
        if (referrerPath) {
            path = JSON.parse(referrerPath);
        }
        if (!path) {
            path = { uri: '/', params: {} };
        }
        /** @type {?} */
        var navigationExtras = {
            queryParams: path.params
        };
        return {
            uri: path.uri,
            extras: navigationExtras
        };
    };
    /**
     * Parses the tokens from the callback URL.
     */
    /**
     * Parses the tokens from the callback URL.
     * @return {?}
     */
    OktaAuthService.prototype.handleAuthentication = /**
     * Parses the tokens from the callback URL.
     * @return {?}
     */
    function () {
        return __awaiter(this, void 0, void 0, function () {
            var tokens, fromUri;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.oktaAuth.token.parseFromUrl()];
                    case 1:
                        tokens = _a.sent();
                        tokens.forEach(function (token) {
                            if (token.idToken) {
                                _this.oktaAuth.tokenManager.add('idToken', token);
                            }
                            if (token.accessToken) {
                                _this.oktaAuth.tokenManager.add('accessToken', token);
                            }
                        });
                        return [4 /*yield*/, this.isAuthenticated()];
                    case 2:
                        if (_a.sent()) {
                            this.emitAuthenticationState(true);
                        }
                        fromUri = this.getFromUri();
                        this.router.navigate([fromUri.uri], fromUri.extras);
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Clears the user session in Okta and removes
     * tokens stored in the tokenManager.
     * @param options
     */
    /**
     * Clears the user session in Okta and removes
     * tokens stored in the tokenManager.
     * @param {?=} options
     * @return {?}
     */
    OktaAuthService.prototype.logout = /**
     * Clears the user session in Okta and removes
     * tokens stored in the tokenManager.
     * @param {?=} options
     * @return {?}
     */
    function (options) {
        return __awaiter(this, void 0, void 0, function () {
            var uri;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        uri = null;
                        options = options || {};
                        if (typeof options === 'string') {
                            uri = options;
                            options = {};
                        }
                        return [4 /*yield*/, this.oktaAuth.signOut(options)];
                    case 1:
                        _a.sent();
                        this.emitAuthenticationState(false);
                        if (!options.postLogoutRedirectUri && !this.config.postLogoutRedirectUri) {
                            this.router.navigate([uri || '/']);
                        }
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Scrub scopes to ensure 'openid' is included
     * @param scopes
     */
    /**
     * Scrub scopes to ensure 'openid' is included
     * @param {?} scopes
     * @return {?}
     */
    OktaAuthService.prototype.scrubScopes = /**
     * Scrub scopes to ensure 'openid' is included
     * @param {?} scopes
     * @return {?}
     */
    function (scopes) {
        if (scopes.indexOf('openid') >= 0) {
            return;
        }
        scopes.unshift('openid');
    };
    OktaAuthService.decorators = [
        { type: Injectable }
    ];
    /** @nocollapse */
    OktaAuthService.ctorParameters = function () { return [
        { type: undefined, decorators: [{ type: Inject, args: [OKTA_CONFIG,] }] },
        { type: Router }
    ]; };
    return OktaAuthService;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var OktaCallbackComponent = /** @class */ (function () {
    function OktaCallbackComponent(okta) {
        this.okta = okta;
        /**
         * Handles the response from Okta and parses tokens.
         */
        okta.handleAuthentication();
    }
    OktaCallbackComponent.decorators = [
        { type: Component, args: [{ template: "" }] }
    ];
    /** @nocollapse */
    OktaCallbackComponent.ctorParameters = function () { return [
        { type: OktaAuthService }
    ]; };
    return OktaCallbackComponent;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var OktaLoginRedirectComponent = /** @class */ (function () {
    function OktaLoginRedirectComponent(okta) {
        this.okta = okta;
        okta.loginRedirect();
    }
    OktaLoginRedirectComponent.decorators = [
        { type: Component, args: [{ template: "" }] }
    ];
    /** @nocollapse */
    OktaLoginRedirectComponent.ctorParameters = function () { return [
        { type: OktaAuthService }
    ]; };
    return OktaLoginRedirectComponent;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var OktaAuthGuard = /** @class */ (function () {
    function OktaAuthGuard(oktaAuth, router) {
        this.oktaAuth = oktaAuth;
        this.router = router;
    }
    /**
     * Gateway for protected route. Returns true if there is a valid accessToken,
     * otherwise it will cache the route and start the login flow.
     * @param route
     * @param state
     */
    /**
     * Gateway for protected route. Returns true if there is a valid accessToken,
     * otherwise it will cache the route and start the login flow.
     * @param {?} route
     * @param {?} state
     * @return {?}
     */
    OktaAuthGuard.prototype.canActivate = /**
     * Gateway for protected route. Returns true if there is a valid accessToken,
     * otherwise it will cache the route and start the login flow.
     * @param {?} route
     * @param {?} state
     * @return {?}
     */
    function (route, state) {
        return __awaiter(this, void 0, void 0, function () {
            var onAuthRequired, path;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.oktaAuth.isAuthenticated()];
                    case 1:
                        if (_a.sent()) {
                            return [2 /*return*/, true];
                        }
                        onAuthRequired = route.data['onAuthRequired'] || this.oktaAuth.getOktaConfig().onAuthRequired;
                        path = state.url.split(/[?#]/)[0];
                        this.oktaAuth.setFromUri(path, route.queryParams);
                        if (onAuthRequired) {
                            onAuthRequired(this.oktaAuth, this.router);
                        }
                        else {
                            this.oktaAuth.loginRedirect();
                        }
                        return [2 /*return*/, false];
                }
            });
        });
    };
    OktaAuthGuard.decorators = [
        { type: Injectable }
    ];
    /** @nocollapse */
    OktaAuthGuard.ctorParameters = function () { return [
        { type: OktaAuthService },
        { type: Router }
    ]; };
    return OktaAuthGuard;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * @param {?} config
 * @param {?} router
 * @return {?}
 */
function createOktaService(config, router) {
    return new OktaAuthService(config, router);
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var ɵ0 = createOktaService;
var OktaAuthModule = /** @class */ (function () {
    function OktaAuthModule() {
    }
    // Deprecated. Your app should provide OKTA_CONFIG directly
    // Deprecated. Your app should provide OKTA_CONFIG directly
    /**
     * @param {?} config
     * @return {?}
     */
    OktaAuthModule.initAuth = 
    // Deprecated. Your app should provide OKTA_CONFIG directly
    /**
     * @param {?} config
     * @return {?}
     */
    function (config) {
        return {
            ngModule: OktaAuthModule,
            providers: [
                // Will NOT provide config when using AOT compiler. Your app module should provide this value statically in its providers section.
                { provide: OKTA_CONFIG, useValue: config }
            ]
        };
    };
    OktaAuthModule.decorators = [
        { type: NgModule, args: [{
                    declarations: [
                        OktaCallbackComponent,
                        OktaLoginRedirectComponent
                    ],
                    exports: [
                        OktaCallbackComponent,
                        OktaLoginRedirectComponent
                    ],
                    providers: [
                        OktaAuthGuard,
                        {
                            provide: OktaAuthService,
                            useFactory: ɵ0,
                            deps: [
                                OKTA_CONFIG,
                                Router
                            ]
                        }
                    ]
                },] }
    ];
    return OktaAuthModule;
}());

export { OKTA_CONFIG, OktaAuthGuard, OktaAuthModule, OktaAuthService, OktaCallbackComponent, OktaLoginRedirectComponent, createOktaService as ɵb };
//# sourceMappingURL=okta-angular.js.map



//////////////////
// WEBPACK FOOTER
// ./~/@okta/okta-angular/fesm5/okta-angular.js
// module id = 70
// module chunks = 0